/** Send Feedback (everyone) and the admin Feedback inbox. */
import { api } from "../api";
import { S, on } from "../state";
import { $, closeSheet, edHead, esc, fmtDateTime, openSheet, toast } from "../ui";

const MAX_FILES = 5;
const MAX_MB = 10;
export const ACCEPT = ".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx";

export const fmtSize = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function openFeedback() {
  openSheet(`${edHead("Send Feedback")}<div class="edbody">
    <section class="edsec"><h3>Your Feedback</h3>
      <p class="muted small" style="margin:-4px 0 0">Something not working, confusing, or a good idea? The admins read every message.</p>
      <textarea id="fbtext" rows="7" maxlength="5000" placeholder="Tell us what happened or what you'd like to see"></textarea></section>
    <section class="edsec"><h3>Attachments</h3>
      <p class="muted small" style="margin:-4px 0 0">Optional. Up to ${MAX_FILES} files, ${MAX_MB} MB each: screenshots, photos, PDFs or Office files.</p>
      <input type="file" id="fbfiles" multiple accept="${ACCEPT}"></section>
    <div class="err" id="err" role="alert"></div></div>
    <div class="edfoot"><button class="btn" data-act="fb-send">Send Feedback</button><button class="btn ghost" data-act="close-sheet">Cancel</button></div>`);
  ($("#fbtext") as HTMLTextAreaElement).focus();
}

/* ---------------- admin inbox ---------------- */

type Fb = {
  id: number;
  userId: number;
  name: string;
  email: string;
  text: string;
  page: string | null;
  createdAt: number;
  dismissed: boolean;
  files: { id: number; filename: string; type: string; size: number }[];
};
const F: { list: Fb[] | null; dismissed: boolean; loading: boolean } = { list: null, dismissed: false, loading: false };

async function load() {
  if (F.loading) return;
  F.loading = true;
  try {
    F.list = await api<Fb[]>(`/api/feedback${F.dismissed ? "?dismissed=1" : ""}`);
  } catch (e) {
    toast((e as Error).message);
  }
  F.loading = false;
  const box = $("#fblist");
  if (box) box.innerHTML = listBody();
}

const fileUrl = (f: Fb, id: number) => `/api/feedback/${f.id}/files/${id}`;

function listBody() {
  if (!F.list) return '<p class="muted">Loading…</p>';
  if (!F.list.length) return `<p class="muted">${F.dismissed ? "No feedback yet." : "Nothing new. You're all caught up."}</p>`;
  return `<div class="tablewrap"><table><thead><tr><th>Received</th><th>From</th><th>Feedback</th><th></th></tr></thead><tbody>${F.list
    .map((f) => {
      const short = f.text.replace(/\s+/g, " ");
      return `<tr class="click" tabindex="0" data-act="fb-open" data-id="${f.id}"><td class="small" style="white-space:nowrap">${fmtDateTime(f.createdAt)}</td>
      <td><b>${esc(f.name)}</b><div class="muted small">${esc(f.email)}</div></td>
      <td>${esc(short.length > 120 ? short.slice(0, 120) + "…" : short)}${f.files.length ? `<div class="muted small">📎 ${f.files.length} attachment${f.files.length > 1 ? "s" : ""}</div>` : ""}</td>
      <td>${f.dismissed ? '<span class="chip">Dismissed</span>' : '<span class="chip blue">New</span>'}</td></tr>`;
    })
    .join("")}</tbody></table></div>`;
}

export function feedbackTab() {
  if (!F.list) void load();
  return `<div class="card"><div class="row wrap" style="gap:8px 10px;margin-bottom:12px;align-items:center">
    <label class="row small" style="gap:6px;cursor:pointer;flex:none;white-space:nowrap"><input type="checkbox" data-change="fb-dismissed" ${F.dismissed ? "checked" : ""}> Show Dismissed</label>
    <span class="muted small" style="margin-left:auto">Dismissed feedback is deleted after 2 years.</span></div>
    <div id="fblist">${listBody()}</div></div>`;
}

function openOne(f: Fb) {
  const subject = encodeURIComponent("Your MCDS Ambassador Scheduling feedback");
  const quoted = encodeURIComponent(`Hi ${f.name.split(" ")[0]},\n\n\n\n> ${f.text.replace(/\n/g, "\n> ")}`);
  openSheet(`${edHead(esc(f.name), "Feedback")}<div class="edbody">
    <div class="small"><b>From:</b> ${esc(f.name)} &lt;${esc(f.email)}&gt;</div>
    <div class="small"><b>Received:</b> ${fmtDateTime(f.createdAt)}${f.page ? ` · <b>Page:</b> ${esc(f.page[0].toUpperCase() + f.page.slice(1))}` : ""}</div>
    <div class="card small" style="white-space:pre-wrap">${esc(f.text)}</div>
    ${
      f.files.length
        ? `<section class="edsec"><h3>Attachments</h3><div class="stack" style="gap:8px">${f.files
            .map(
              (x) => `<div class="row wrap" style="gap:8px"><a href="${fileUrl(f, x.id)}" target="_blank" rel="noopener">${esc(x.filename)}</a><span class="muted small">${fmtSize(x.size)}</span>
              <a class="small" href="${fileUrl(f, x.id)}?download=1">Download</a></div>${
                x.type.startsWith("image/") && x.type !== "image/heic" ? `<img src="${fileUrl(f, x.id)}" alt="${esc(x.filename)}" style="max-width:100%;border-radius:10px;border:1px solid var(--line)">` : ""
              }`,
            )
            .join("")}</div></section>`
        : ""
    }</div>
    <div class="edfoot"><a class="btn" href="mailto:${esc(f.email)}?subject=${subject}&body=${quoted}">Reply by Email</a>${
      f.dismissed ? `<button class="btn ghost" data-act="fb-reopen" data-id="${f.id}">Reopen</button>` : `<button class="btn ghost" data-act="fb-dismiss" data-id="${f.id}">Dismiss</button>`
    }<button class="btn ghost" data-act="close-sheet">Close</button></div>`);
}

on({
  "open-feedback": () => openFeedback(),
  "fb-send": async (el) => {
    const text = ($("#fbtext") as HTMLTextAreaElement).value.trim();
    const files = Array.from(($("#fbfiles") as HTMLInputElement).files || []);
    const err = $("#err")!;
    if (!text) return (err.textContent = "Type your feedback first.");
    if (files.length > MAX_FILES) return (err.textContent = `Attach up to ${MAX_FILES} files.`);
    const big = files.find((f) => f.size > MAX_MB * 1048576);
    if (big) return (err.textContent = `${big.name} is too big. The limit is ${MAX_MB} MB.`);
    const fd = new FormData();
    fd.set("text", text);
    fd.set("page", S.route);
    for (const f of files) fd.append("files", f);
    (el as HTMLButtonElement).disabled = true;
    try {
      await api("/api/feedback", { form: fd });
      closeSheet();
      toast("Thank you! Your feedback was sent.");
      F.list = null;
    } catch (e) {
      err.textContent = (e as Error).message;
      (el as HTMLButtonElement).disabled = false;
    }
  },
  "fb-dismissed": (el) => {
    F.dismissed = (el as HTMLInputElement).checked;
    F.list = null;
    $("#fblist")!.innerHTML = listBody();
    void load();
  },
  "fb-open": (el) => {
    const f = F.list?.find((x) => x.id === Number(el.dataset.id));
    if (f) openOne(f);
  },
  "fb-dismiss": (el) => void setDismissed(Number(el.dataset.id), true),
  "fb-reopen": (el) => void setDismissed(Number(el.dataset.id), false),
});

async function setDismissed(id: number, dismissed: boolean) {
  try {
    await api(`/api/feedback/${id}/${dismissed ? "dismiss" : "reopen"}`, { body: {} });
    closeSheet();
    toast(dismissed ? "Dismissed." : "Reopened.");
    F.list = null;
    $("#fblist") && ($("#fblist")!.innerHTML = listBody());
    void load();
  } catch (e) {
    toast((e as Error).message);
  }
}
